import { useEffect, useRef } from 'react';

import { useUser } from '@clerk/react';
import {
  TEAM_ANCHOR_ONLY_FIELDS,
  TEAM_BILLING_CACHE_FIELDS,
  Team,
  TeamRole,
  WebSocketEntityType,
} from '@this+that/models';

import {
  useGetPaginatedTeamMembers,
  useGetTeamById,
} from '../../../services/team.service';
import { useTeamStore } from '../../../store/Team/useTeamStore';
import {
  useWebSocket,
  WebSocketEventType,
} from '../../WebSocket/WebSocketContext';

const MEMBERS_PAGE_LIMIT = 50;

// Attributes that describe the TEAM rather than the member whose row carries
// them. `createdAt` is deliberately absent: every row has its own, and the
// cached team's is the viewer's own join date.
const TEAM_LEVEL_FIELDS = [
  'name',
  'hexColor',
  'createdFor',
  'trialEndsAt',
  ...TEAM_BILLING_CACHE_FIELDS,
  ...TEAM_ANCHOR_ONLY_FIELDS,
] as const;

function pickTeamFields(row: Team): Partial<Team> {
  const patch: Partial<Team> = {};
  TEAM_LEVEL_FIELDS.forEach((field) => {
    if (field in row) {
      (patch as Record<string, unknown>)[field] = row[field as keyof Team];
    }
  });
  return patch;
}

export function useTeamSync() {
  const { subscribe } = useWebSocket();
  const { user } = useUser();
  const { getTeamById } = useGetTeamById();
  const { getPaginatedTeamMembers } = useGetPaginatedTeamMembers();

  const getTeamByIdRef = useRef(getTeamById);
  const getPaginatedTeamMembersRef = useRef(getPaginatedTeamMembers);
  getTeamByIdRef.current = getTeamById;
  getPaginatedTeamMembersRef.current = getPaginatedTeamMembers;

  useEffect(() => {
    const currentUserId = user?.id;

    const unsubscribeInsert = subscribe<Team>(
      WebSocketEntityType.Team,
      WebSocketEventType.INSERT,
      async ({ item }) => {
        if (!item?.id || !item.userId) {
          return;
        }

        if (item.userId === currentUserId) {
          const [team, membersPage] = await Promise.all([
            getTeamByIdRef.current(item.id),
            getPaginatedTeamMembersRef.current(item.id, MEMBERS_PAGE_LIMIT),
          ]);

          if (!team) {
            return;
          }

          const members = membersPage?.members ?? [];
          useTeamStore.getState().setTeamData(item.id, team, members);
          useTeamStore.getState().addUserTeamId(item.id);
        } else {
          const { teamsById } = useTeamStore.getState();
          if (teamsById[item.id]) {
            useTeamStore.getState().upsertTeamMember(item.id, item);
          }
        }
      },
    );

    const unsubscribeModify = subscribe<Team>(
      WebSocketEntityType.Team,
      WebSocketEventType.MODIFY,
      ({ item }) => {
        if (!item?.id || !item.userId) {
          return;
        }

        const { teamsById } = useTeamStore.getState();
        if (!teamsById[item.id]) {
          return;
        }

        useTeamStore.getState().upsertTeamMember(item.id, item);

        // The owner row is the only one carrying team-level metadata, but it
        // also carries the OWNER's identity and role. Spreading the whole row
        // onto the cached team handed every recipient `userRole: OWNER` — and
        // with it the delete-team controls. Take the team fields only.
        if (item.userRole === TeamRole.OWNER) {
          useTeamStore.getState().patchTeam(item.id, pickTeamFields(item));
        }

        // `teamsById[id].team` IS the viewer's own row — `myRole`, `canManage`
        // and `isOwner` all read from it, so a change to my own row has to
        // land there too or my permissions stay stale until a reload.
        if (item.userId === currentUserId) {
          useTeamStore.getState().patchTeam(item.id, {
            userRole: item.userRole,
            userFeatures: item.userFeatures,
          });
        }
      },
    );

    const unsubscribeRemove = subscribe<Team>(
      WebSocketEntityType.Team,
      WebSocketEventType.REMOVE,
      ({ item }) => {
        if (!item?.id || !item.userId) {
          return;
        }

        if (item.userId === currentUserId) {
          useTeamStore.getState().removeTeam(item.id);
        } else {
          useTeamStore.getState().removeMemberFromTeam(item.id, item.userId);
        }
      },
    );

    return () => {
      unsubscribeInsert();
      unsubscribeModify();
      unsubscribeRemove();
    };
  }, [subscribe, user?.id]);
}
